/**
 * Calendar Utility Functions
 *
 * Date math, grid builders, overlap layout, and recurrence label generation.
 */

import type {
  EventInstance,
  CalendarView,
  RecurrenceRule,
  CalendarSettings,
  QuickEventDraft,
  TemperatureUnit,
  RecurrenceEditScope,
} from './types';
import { DAY_NAMES, MONTH_NAMES, FREQUENCY_LABELS, HOUR_HEIGHT_PX } from './types';

// =============================================================================
// DATE MATH
// =============================================================================

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function startOfWeek(date: Date, weekStartDay: number = 0): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day - weekStartDay + 7) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfWeek(date: Date, weekStartDay: number = 0): Date {
  const d = startOfWeek(date, weekStartDay);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function endOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// =============================================================================
// VISIBLE RANGE COMPUTATION
// =============================================================================

export function getVisibleRange(
  currentDate: Date,
  view: CalendarView,
  settings: CalendarSettings,
): { start: Date; end: Date } {
  switch (view) {
    case 'month': {
      // Month grid starts at the beginning of the first visible week
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const gridStart = startOfWeek(monthStart, settings.weekStartDay);
      const gridEnd = endOfWeek(monthEnd, settings.weekStartDay);
      return { start: gridStart, end: gridEnd };
    }
    case 'week': {
      const weekStart = startOfWeek(currentDate, settings.weekStartDay);
      const weekEnd = endOfWeek(currentDate, settings.weekStartDay);
      return { start: weekStart, end: weekEnd };
    }
    case 'day': {
      return { start: startOfDay(currentDate), end: endOfDay(currentDate) };
    }
    case 'agenda': {
      const agendaStart = startOfDay(currentDate);
      const agendaEnd = addDays(agendaStart, settings.agendaDaysAhead);
      return { start: agendaStart, end: agendaEnd };
    }
  }
}

// =============================================================================
// GRID BUILDERS
// =============================================================================

export interface MonthGridCell {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: EventInstance[];
}

/**
 * Does `event` occupy the calendar day `dayDate` represents?
 *
 * All-day events are stored as UTC-midnight instants with an EXCLUSIVE end —
 * an all-day event on Aug 13 is `2026-08-13T00:00Z → 2026-08-14T00:00Z`,
 * mirroring the RFC 5545 DATE form Google and CalDAV emit. They are floating
 * DATES, not instants, so they must never be converted through the viewer's
 * timezone: west of UTC, `00:00Z` renders as 18:00 the previous evening, and
 * a one-day event then overlaps two local days and is drawn twice (observed in
 * QA in America/Denver — one chip on Aug 12, one on Aug 13). Compare calendar
 * dates in UTC instead.
 *
 * Timed events ARE real instants and belong in local time, so they keep the
 * local-boundary overlap test.
 */
/**
 * All-day events are DATES, not instants, and the platform stores them the way
 * every provider does: UTC midnight for the start, and an EXCLUSIVE UTC
 * midnight for the end (a one-day event on the 26th ends on the 27th).
 * `eventOccupiesDay` below reads them back with UTC date components.
 *
 * The form must use the same convention, which is why these three helpers live
 * next to that reader rather than inside the dialog. Encoding an all-day range
 * from LOCAL midnight instead put every user east of UTC one day early — the
 * stored instant fell on the previous UTC date, so the chip rendered on the
 * wrong day AND the wrong date was pushed to Outlook and iCloud. It looked
 * correct in the Americas, which is the only reason it survived QA.
 */
const ALL_DAY_MS = 86_400_000;

/**
 * Timed events carry their own IANA timezone (`vault_calendar_events.timezone`,
 * and Graph sends one on every event). The form used to interpret its date and
 * time inputs in whatever zone the BROWSER happened to be in, so an event
 * created for a Tokyo meeting while travelling was stored at the traveller's
 * wall-clock time, and reopening an event abroad silently rewrote it on save.
 *
 * These convert between the paired date/time inputs and the stored instant in
 * an explicit zone. `Intl` is the only source of truth for offsets — hard-coded
 * tables go stale, and DST rules change by legislation.
 */
/**
 * A zone `Intl` will actually accept, falling back to the viewer's.
 *
 * An event can arrive carrying a zone this browser does not know — most
 * plausibly a Windows id that the Windows->IANA mapping did not cover, which is
 * exactly the class of data this sync work introduces. `Intl.DateTimeFormat`
 * throws `RangeError` on one, and the conversions below run inside the event
 * dialog's open effect, so the throw unwound to the nearest error boundary
 * instead of producing anything the user could act on: the dialog simply would
 * not open.
 *
 * Falling back is right here because this is a DISPLAY concern — the stored
 * instant is unaffected, and showing the event in the viewer's zone beats
 * showing nothing. The push path takes the opposite line and refuses, because
 * there a wrong zone silently reschedules the event on the real calendar.
 */
export function usableTimeZone(timeZone: string | null | undefined): string {
  if (!timeZone) return browserTimeZone();
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0);
    return timeZone;
  } catch {
    return browserTimeZone();
  }
}

function timeZoneOffsetMs(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const at = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  // `hour` comes back as 24 at midnight under hour12:false in some engines.
  const asIfUtc = Date.UTC(
    at('year'),
    at('month') - 1,
    at('day'),
    at('hour') % 24,
    at('minute'),
    at('second'),
  );
  return asIfUtc - instant;
}

/** The wall-clock the user typed, read in `timeZone`, as a stored instant. */
export function zonedInputsToUtcIso(date: string, time: string, timeZone: string): string {
  const zone = usableTimeZone(timeZone);
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = (time || '00:00').split(':').map(Number);
  const naive = Date.UTC(y, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  // Two passes: the first offset is sampled at the wrong instant when the guess
  // lands on the other side of a DST transition, the second corrects it.
  let instant = naive - timeZoneOffsetMs(naive, zone);
  instant = naive - timeZoneOffsetMs(instant, zone);

  // A wall time inside a spring-forward GAP does not exist, and the two passes
  // resolve it in whichever direction the zone's transition happens to fall —
  // America/New_York landed an hour EARLIER than the user typed while
  // Europe/Berlin landed an hour later. Detect it by reading the instant back:
  // if it does not render as the requested wall time, the time was skipped, so
  // shift FORWARD past the gap. That matches what calendars do with a
  // nonexistent time and is at least consistent across zones.
  // A wall time inside a spring-forward GAP does not exist, and the two passes
  // then resolve it in whichever direction the zone's transition happens to
  // fall — America/New_York landed an hour EARLIER than the user typed while
  // Europe/Berlin landed an hour later. Detect it by reading the instant back:
  // if it does not render as the requested wall time, the time was skipped.
  //
  // Resolve a skipped time with the offset in force BEFORE the transition,
  // which lands just past the gap in every zone (02:30 -> 03:30). That is what
  // calendars do with a nonexistent time, and it is at least the same answer
  // everywhere rather than zone-dependent.
  if (instant + timeZoneOffsetMs(instant, zone) !== naive) {
    instant = naive - timeZoneOffsetMs(naive - 24 * 60 * 60 * 1000, zone);
  }

  return new Date(instant).toISOString();
}

/** A stored instant as the wall-clock date and time inputs for `timeZone`. */
export function utcIsoToZonedInputs(iso: string, timeZone: string): { date: string; time: string } {
  const zone = usableTimeZone(timeZone);
  const instant = new Date(iso).getTime();
  // `Intl.formatToParts` throws RangeError on an invalid Date, and this runs
  // inside the dialog's save path — an unparseable instant took the whole save
  // down with "Invalid time value" rather than doing anything useful.
  if (!Number.isFinite(instant)) return { date: '', time: '' };
  const shifted = new Date(instant + timeZoneOffsetMs(instant, zone));
  return {
    date: formatUtcDateForInput(shifted),
    time: `${String(shifted.getUTCHours()).padStart(2, '0')}:${String(
      shifted.getUTCMinutes(),
    ).padStart(2, '0')}`,
  };
}

/** The viewer's own zone — the default for a new event, as everywhere else. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Zones offered before the user types anything.
 *
 * The full IANA database is ~450 zones, most of them aliases or places nobody
 * schedules against ("America/Indiana/Petersburg"), and a flat list of them is
 * unusable — a US user wants four entries, not four hundred. This is the
 * shortlist; SEARCH still runs over the complete `supportedTimeZones()` list,
 * so nothing is unreachable and an event already in an obscure zone can always
 * keep it. Curating the default view is a UI decision; removing zones would be
 * a correctness one.
 */
export const COMMON_TIME_ZONES: ReadonlyArray<{ zone: string; label: string }> = [
  { zone: 'America/Los_Angeles', label: 'Pacific Time' },
  { zone: 'America/Denver', label: 'Mountain Time' },
  { zone: 'America/Phoenix', label: 'Arizona' },
  { zone: 'America/Chicago', label: 'Central Time' },
  { zone: 'America/New_York', label: 'Eastern Time' },
  { zone: 'America/Anchorage', label: 'Alaska Time' },
  { zone: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { zone: 'America/Toronto', label: 'Toronto' },
  { zone: 'America/Vancouver', label: 'Vancouver' },
  { zone: 'America/Mexico_City', label: 'Mexico City' },
  { zone: 'America/Bogota', label: 'Bogotá' },
  { zone: 'America/Sao_Paulo', label: 'São Paulo' },
  { zone: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires' },
  { zone: 'UTC', label: 'Coordinated Universal Time' },
  { zone: 'Europe/London', label: 'London' },
  { zone: 'Europe/Dublin', label: 'Dublin' },
  { zone: 'Europe/Lisbon', label: 'Lisbon' },
  { zone: 'Europe/Madrid', label: 'Madrid' },
  { zone: 'Europe/Paris', label: 'Paris' },
  { zone: 'Europe/Berlin', label: 'Berlin' },
  { zone: 'Europe/Rome', label: 'Rome' },
  { zone: 'Europe/Amsterdam', label: 'Amsterdam' },
  { zone: 'Europe/Stockholm', label: 'Stockholm' },
  { zone: 'Europe/Warsaw', label: 'Warsaw' },
  { zone: 'Europe/Athens', label: 'Athens' },
  { zone: 'Europe/Istanbul', label: 'Istanbul' },
  { zone: 'Europe/Moscow', label: 'Moscow' },
  { zone: 'Africa/Lagos', label: 'Lagos' },
  { zone: 'Africa/Cairo', label: 'Cairo' },
  { zone: 'Africa/Nairobi', label: 'Nairobi' },
  { zone: 'Africa/Johannesburg', label: 'Johannesburg' },
  { zone: 'Asia/Dubai', label: 'Dubai' },
  { zone: 'Asia/Karachi', label: 'Karachi' },
  { zone: 'Asia/Kolkata', label: 'India' },
  { zone: 'Asia/Dhaka', label: 'Dhaka' },
  { zone: 'Asia/Bangkok', label: 'Bangkok' },
  { zone: 'Asia/Jakarta', label: 'Jakarta' },
  { zone: 'Asia/Singapore', label: 'Singapore' },
  { zone: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { zone: 'Asia/Shanghai', label: 'China' },
  { zone: 'Asia/Seoul', label: 'Seoul' },
  { zone: 'Asia/Tokyo', label: 'Japan' },
  { zone: 'Australia/Perth', label: 'Perth' },
  { zone: 'Australia/Brisbane', label: 'Brisbane' },
  { zone: 'Australia/Sydney', label: 'Sydney' },
  { zone: 'Pacific/Auckland', label: 'Auckland' },
];

const COMMON_LABEL_BY_ZONE = new Map(COMMON_TIME_ZONES.map((t) => [t.zone, t.label]));

/** "Pacific Time" for a curated zone, otherwise the city out of the IANA id. */
export function timeZoneLabel(zone: string): string {
  const curated = COMMON_LABEL_BY_ZONE.get(zone);
  if (curated) return curated;
  const city = zone.split('/').pop() ?? zone;
  return city.replace(/_/g, ' ');
}

/**
 * Current UTC offset of a zone, e.g. "GMT-6" — the thing people actually
 * recognise a zone by, and the only way to tell two same-named cities apart.
 * Offsets shift with DST, so this is computed for `at`, never cached.
 */
export function timeZoneOffsetLabel(zone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    }).formatToParts(at);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Every zone the browser knows. Used for SEARCH, so a zone outside the curated
 * shortlist is still reachable by typing. Where `supportedValuesOf` is missing,
 * fall back to the shortlist rather than an empty result.
 */
export function supportedTimeZones(): string[] {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    if (supported?.length) return supported;
  } catch {
    /* fall through */
  }
  return COMMON_TIME_ZONES.map((t) => t.zone);
}

/**
 * Fields a change to which cannot be stored on a SINGLE occurrence.
 *
 * `vault_calendar_event_exceptions` carries title, description, location,
 * start/end, all-day, status, colour and reminders — and nothing else. An edit
 * scoped to one occurrence therefore cannot express a new URL, tags, a move to
 * another calendar, a timezone, or a recurrence change; those belong to the
 * series row. The mutation used to accept them and drop them on the floor,
 * reporting success.
 */
const SERIES_ONLY_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'url', label: 'the link' },
  { key: 'tags', label: 'tags' },
  { key: 'calendarId', label: 'the calendar' },
  { key: 'timezone', label: 'the timezone' },
  { key: 'rrule', label: 'the repeat rule' },
];

/** Which series-only fields this edit would change, by user-facing label. */
/**
 * Canonical RRULE for COMPARISON only — part order, case, set-member order and
 * an explicit INTERVAL=1 folded away.
 *
 * RFC 5545 fixes no part order and no case, and the dialog round-trips the rule
 * through the recurrence builder, which re-serialises in its own order. Raw
 * comparison therefore reported a recurrence change on an event whose repeat
 * rule the user never opened. Mirrors `canonicalRrule` in the sync engine's
 * normalize.ts, which exists for the same reason on the provider side.
 */
function canonicalRruleForCompare(value: unknown): unknown {
  if (typeof value !== 'string' || value.length === 0) return value;
  const parts = new Map<string, string>();
  for (const segment of value.split(';')) {
    const trimmed = segment.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf('=');
    if (eq <= 0) return value; // Malformed — compare raw rather than guess.
    const key = trimmed.slice(0, eq).toUpperCase();
    let part = trimmed.slice(eq + 1).toUpperCase();
    if (key.startsWith('BY'))
      part = part
        .split(',')
        .map((m) => m.trim())
        .filter(Boolean)
        .sort()
        .join(',');
    if (key === 'INTERVAL' && part === '1') continue;
    parts.set(key, part);
  }
  if (parts.size === 0) return value;
  return [...parts.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${k}=${v}`)
    .join(';');
}

/**
 * Empty is empty. The event row carries `''` for an unset link while the form
 * sends `undefined`, and treating those as different reported "the link
 * changed" to a user who had only nudged an occurrence by five minutes.
 */
function emptyToNull(value: unknown): unknown {
  return value === '' || value === undefined ? null : value;
}

export function seriesOnlyChanges(
  original: Record<string, unknown>,
  next: Record<string, unknown>,
): string[] {
  return SERIES_ONLY_FIELDS.filter(({ key }) => {
    // `undefined` in a PATCH means "leave this alone", NOT "clear it" — which
    // is exactly how `updateVaultCalendarEvent` reads it. Treating an omitted
    // field as a change refused a five-minute nudge with "Changing the
    // timezone applies to the whole series", because the dialog omits
    // `timezone` while the column is NOT NULL DEFAULT 'UTC' and so never
    // matches null. Same shape for all-day events, which always omit it.
    if (!(key in next) || next[key] === undefined) return false;
    let before = emptyToNull(original[key]);
    let after = emptyToNull(next[key]);
    // Tags compare as sets — order is not a change.
    if (Array.isArray(before) || Array.isArray(after)) {
      const norm = (v: unknown) => JSON.stringify([...((v as string[]) ?? [])].sort());
      return norm(before) !== norm(after);
    }
    if (key === 'rrule') {
      before = canonicalRruleForCompare(before);
      after = canonicalRruleForCompare(after);
    }
    return before !== after;
  }).map((f) => f.label);
}

/** "a", "a and b", "a, b and c" — for a sentence, not a list. */
export function formatList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export const RRULE_WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const;

/**
 * What moving a series start means for its recurrence rule.
 *
 * `unsupported` is the reason this is a union rather than `string | null`. Null
 * conflated "the rule already matches" with "I cannot express this move", and
 * the callers treated both as go-ahead — so a rule the comments called a
 * refusal was submitted anyway as a start-only update. For Microsoft that is
 * the silent-discard path: 200 OK, nothing moves, reverted on the next pull.
 */
export type RuleAdjustment =
  | { kind: 'unchanged' }
  | { kind: 'rewritten'; rrule: string }
  | { kind: 'unsupported'; reason: string };

/**
 * The zone a series' rule should be read in.
 *
 * An all-day event is a DATE stored at UTC midnight, and the platform moves it
 * in whole UTC days — so its weekday and day-of-month are UTC facts. Reading
 * one in the viewer's zone puts every user WEST of UTC a day early:
 * `2026-08-20T00:00:00Z` is the 19th at 18:00 in Denver.
 *
 * A TIMED event is the opposite: its weekday is whatever the user was looking
 * at when they entered it, so it reads in the picker's zone for a form edit and
 * the viewer's for a drag.
 */
function ruleZoneFor(isAllDay: boolean | undefined, timeZone: string | null | undefined): string {
  return isAllDay ? 'UTC' : usableTimeZone(timeZone ?? browserTimeZone());
}

/** Days in a calendar month (1-indexed month). */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Calendar y/m/d of an instant, read in `zone`, plus the rule-relevant derivations. */
function calendarPartsIn(
  iso: string,
  zone: string,
): {
  y: number;
  m: number;
  d: number;
  weekday: string;
  /** 1-5: which occurrence of that weekday this is within the month. */
  ordinal: number;
  /** -1..-5: the same, counted from the end. */
  ordinalFromEnd: number;
  /** -1..-31: day of month counted from the end, so the last day is -1. */
  dayFromEnd: number;
  /** The instant itself, for UNTIL comparison. */
  ms: number;
} | null {
  const ms = new Date(iso).getTime();
  const { date } = utcIsoToZonedInputs(iso, zone);
  if (!date || Number.isNaN(ms)) return null;
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return null;
  const total = daysInMonth(y, m);
  return {
    y,
    m,
    d,
    weekday: RRULE_WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()],
    ordinal: Math.ceil(d / 7),
    ordinalFromEnd: -(Math.floor((total - d) / 7) + 1),
    dayFromEnd: d - total - 1,
    ms,
  };
}

type CalendarParts = NonNullable<ReturnType<typeof calendarPartsIn>>;

/** `BYDAY` token -> its ordinal (RFC allows a `+` sign) and weekday. */
function splitByDayToken(token: string): { ordinal: number | null; weekday: string } {
  const t = token.trim().toUpperCase();
  const match = /^([+-]?\d+)/.exec(t);
  return {
    ordinal: match ? Number(match[1]) : null,
    weekday: t.replace(/^[+-]?\d+/, ''),
  };
}

function listValues(parts: string[], name: string): string[] | null {
  const part = parts.find((p) => p.toUpperCase().startsWith(`${name}=`));
  if (!part) return null;
  return (part.split('=')[1] ?? '').split(',').filter(Boolean);
}

/**
 * Does this rule already generate an occurrence on `at`?
 *
 * The whole decision now turns on this one question, asked BEFORE anything
 * else. If the rule already produces the start there is nothing to reconcile,
 * whatever shape the rule is and however many values it pins — so a Mon/Wed/Fri
 * series starting on a Wednesday, a "1st and 15th" series starting on the 15th,
 * and a twice-yearly June/December series are all simply left alone.
 *
 * Deliberately conservative: anything it cannot confidently evaluate returns
 * false, which routes to a refusal rather than to a rewrite.
 */
function ruleProduces(parts: string[], freq: string, at: CalendarParts): boolean {
  if (freq === 'WEEKLY') {
    const byDay = listValues(parts, 'BYDAY');
    // RFC 5545 defaults BYDAY to DTSTART's weekday, so an absent one always
    // matches.
    if (!byDay) return true;
    return byDay.some((t) => splitByDayToken(t).weekday === at.weekday);
  }

  // MONTHLY / YEARLY.
  const byMonth = listValues(parts, 'BYMONTH');
  if (byMonth && !byMonth.map(Number).includes(at.m)) return false;

  const byMonthDay = listValues(parts, 'BYMONTHDAY');
  const byDay = listValues(parts, 'BYDAY');

  // Neither pinned: both the RFC and Graph derive the day from DTSTART, so the
  // rule follows the start by construction.
  if (!byMonthDay && !byDay) return true;

  if (byMonthDay) {
    const days = byMonthDay.map(Number);
    if (!days.some((n) => n === at.d || n === at.dayFromEnd)) return false;
  }

  if (byDay) {
    const setPos = listValues(parts, 'BYSETPOS')?.map(Number) ?? null;
    const matches = byDay.some((token) => {
      const { ordinal, weekday } = splitByDayToken(token);
      if (weekday !== at.weekday) return false;
      const positions = ordinal !== null ? [ordinal] : setPos;
      // No ordinal on the token and no BYSETPOS: "every <weekday>".
      if (!positions) return true;
      return positions.some((n) => n === at.ordinal || n === at.ordinalFromEnd);
    });
    if (!matches) return false;
  }

  return true;
}

/**
 * The instant a series' own `UNTIL` ends, or null when it has none.
 *
 * RFC 5545 allows a DATE-only form, which means through the END of that day.
 * The previous inline parser rewrote it into `2026-09-01T::Z` — an invalid
 * timestamp `Date.parse` rejects — so the guard silently never fired for it.
 */
function untilInstantMs(parts: string[]): number | null {
  const raw = parts
    .find((p) => p.toUpperCase().startsWith('UNTIL='))
    ?.split('=')[1]
    ?.trim();
  if (!raw) return null;
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2}))?Z?$/i.exec(raw);
  if (!m) return null;
  const [, y, mo, d, hh, mi, ss] = m;
  const ms = Date.parse(`${y}-${mo}-${d}T${hh ?? '23'}:${mi ?? '59'}:${ss ?? '59'}Z`);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * A recurrence rule reconciled with a new series start.
 *
 * ── Why this is deliberately narrow ─────────────────────────────────────────
 * A series master whose start its own rule excludes is a real problem. Graph
 * REJECTS a weekly one, and — measured live 2026-08-28 — silently DISCARDS a
 * start-only monthly update: 200 OK, the start unchanged, and the next pull
 * reverts the move locally too, so the user's drag quietly snaps back.
 *
 * The obvious fix is to rewrite the rule to match the new start. That was
 * tried, generalised across BYMONTHDAY / BYSETPOS / ordinal BYDAY / BYMONTH,
 * and reverted: rewriting an arbitrary RRULE is a much harder problem than the
 * defect warranted, and every version of it silently DELETED occurrences.
 * Measured examples, all from real rule shapes:
 *
 *   BYDAY=1TH;BYSETPOS=1 -> BYSETPOS=3   BYSETPOS indexes a one-element set;
 *                                        the series expands to nothing.
 *   ...;UNTIL=20261115   -> start Nov 20 past its own UNTIL; nothing.
 *   BYMONTHDAY=15        -> BYMONTHDAY=31 skips every short month.
 *   BYDAY=WE             -> BYDAY=3TH     invents an ordinal, drops 3 in 4.
 *   BYDAY=1MO            -> BYDAY=5MO     Graph cannot express a 5th.
 *
 * Losing a MOVE is bad; losing the series is worse, and it happened behind a
 * success toast. So the rule is never reshaped. Either it already produces the
 * new start — in which case nothing needs doing — or the move is refused and
 * the user is told to edit the repeat rule, which is the control that actually
 * expresses it. The single-day WEEKLY case is the one exception: it is the
 * common drag, its intent is unambiguous, and it predates all of this.
 */
export function ruleForNewStart(
  rrule: string | null,
  startIso: string,
  options: {
    /**
     * Zone the times were ENTERED in — the picker's for a form edit, the
     * viewer's for a drag. Ignored for an all-day event, which is a UTC date.
     */
    timeZone?: string | null;
    /**
     * Start this edit began from.
     *
     * ABSENT MEANS THERE IS NO MOVE — a create, or a template. Nothing is
     * adjusted and nothing is refused: the user is authoring a rule, not moving
     * a series, and providers accept a start their rule excludes by advancing
     * to the first occurrence.
     */
    previousStartIso?: string | null;
    isAllDay?: boolean;
  } = {},
): RuleAdjustment {
  if (!rrule) return { kind: 'unchanged' };
  const { timeZone, previousStartIso, isAllDay } = options;

  // Strip the `RRULE:` property prefix before anything reads FREQ. The vault
  // holds both forms — calendar-import writes the prefixed one — and without
  // this `parts[0]` is 'RRULE:FREQ=MONTHLY', no FREQ is found, and the whole
  // guard silently no-ops for every imported series. Every other consumer
  // strips it (parseRruleToRule, the Graph mapper, the expander).
  const parts = rrule
    .replace(/^RRULE:/i, '')
    .split(';')
    .map((p) => p.trim())
    .filter(Boolean);
  const freq = parts
    .find((p) => p.toUpperCase().startsWith('FREQ='))
    ?.split('=')[1]
    ?.toUpperCase();
  // DAILY and anything unrecognised pin no day, so they follow the start.
  if (freq !== 'WEEKLY' && freq !== 'MONTHLY' && freq !== 'YEARLY') {
    return { kind: 'unchanged' };
  }

  const zone = ruleZoneFor(isAllDay, timeZone);
  const at = calendarPartsIn(startIso, zone);
  // An unreadable start is not a licence to guess — nor to push the old rule
  // against a start it excludes.
  if (!at) return { kind: 'unsupported', reason: 'The new start date could not be read.' };

  // 1. No move at all — a create, or a template stamping `startAt: now` onto a
  //    saved pattern. The user is authoring a rule, not moving a series, so
  //    nothing is adjusted AND nothing is refused. First, because every check
  //    below reasons about a move: running the end-boundary check ahead of it
  //    raised "Cannot move the whole series" on a brand-new event whose
  //    template carried a stale UNTIL.
  const before = previousStartIso ? calendarPartsIn(previousStartIso, zone) : null;
  if (!before) return { kind: 'unchanged' };

  // 2. Past the series' own end. Checked before the satisfied fast-path,
  //    because it is true regardless of what the rule thinks of the weekday:
  //    a start after UNTIL leaves the series with NO occurrences. Moving a
  //    Wednesday series to a LATER Wednesday satisfies `ruleProduces`, so it
  //    returned `unchanged` and sailed straight past the old check below.
  const until = untilInstantMs(parts);
  if (until !== null && at.ms > until) {
    return {
      kind: 'unsupported',
      reason: 'The new date is after this series ends, so no occurrences would remain.',
    };
  }

  // 3. Already satisfied — the only other question whose answer is always safe.
  if (ruleProduces(parts, freq, at)) return { kind: 'unchanged' };

  // 4. The old start did not satisfy the rule either, so this edit did not
  //    break the pairing and is not ours to repair — the rule arrived that way
  //    from a provider, or the user authored it so deliberately, and expanders
  //    accept it by advancing to the first occurrence.
  if (!ruleProduces(parts, freq, before)) return { kind: 'unchanged' };

  // 5. The start really has moved off its own rule.

  if (freq === 'WEEKLY') {
    const byDayIndex = parts.findIndex((p) => p.toUpperCase().startsWith('BYDAY='));
    const days = parts[byDayIndex].split('=')[1].split(',').filter(Boolean);
    // Which of Mon/Wed/Fri the user meant to move is a guess.
    if (days.length !== 1) {
      return {
        kind: 'unsupported',
        reason:
          'This series repeats on several weekdays. Edit the repeat rule to move the whole series.',
      };
    }
    const next = [...parts];
    next[byDayIndex] = `BYDAY=${at.weekday}`;
    return { kind: 'rewritten', rrule: next.join(';') };
  }

  return {
    kind: 'unsupported',
    reason:
      'This series repeats on a monthly or yearly pattern. Edit the repeat rule to move the whole series.',
  };
}

/**
 * Weekly-only entry point, kept for its own tests and older callers.
 * Delegates: the dispatch below is the single implementation.
 */
export function weeklyRuleForNewStart(
  rrule: string | null,
  startIso: string,
  timeZone?: string | null,
  previousStartIso?: string | null,
  isAllDay?: boolean,
): RuleAdjustment {
  return ruleForNewStart(rrule, startIso, { timeZone, previousStartIso, isAllDay });
}

/** UTC date part of an instant, formatted for `<input type="date">`. */
export function formatUtcDateForInput(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate(),
  ).padStart(2, '0')}`;
}

/**
 * Encode an all-day range picked in the form as stored instants: UTC midnight
 * start, exclusive UTC midnight end.
 */
export function encodeAllDayRange(
  startDate: string,
  endDate: string,
): { startAt: string; endAt: string } {
  const startAt = new Date(`${startDate}T00:00:00.000Z`);
  const endExclusive = new Date(new Date(`${endDate}T00:00:00.000Z`).getTime() + ALL_DAY_MS);
  return { startAt: startAt.toISOString(), endAt: endExclusive.toISOString() };
}

/**
 * The INCLUSIVE last date an all-day event occupies — what the form's "End
 * date" field should show.
 *
 * Mirrors `eventOccupiesDay`'s span exactly, including its floor, so the date
 * the user sees is the last day the grid actually draws a chip on. That also
 * makes it correct for rows written under older conventions (a local-midnight
 * `23:59:59.999` end) without needing to know which convention wrote them.
 */
export function allDayLastDate(startAt: string, endAt: string): Date {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const startUtc = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const endUtc = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  return new Date(Math.max(endUtc, startUtc + ALL_DAY_MS) - ALL_DAY_MS);
}

export function eventOccupiesDay(event: EventInstance, dayDate: Date): boolean {
  const start = new Date(event.startAt);
  const end = new Date(event.endAt);

  if (!event.isAllDay) {
    return start < endOfDay(dayDate) && end > startOfDay(dayDate);
  }

  // The cell's calendar date, as a UTC instant — compared against the event's
  // UTC date components so neither side passes through a timezone offset.
  const dayUtc = Date.UTC(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
  const startUtc = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const endUtc = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());

  // End is exclusive. Some providers emit end == start for a single all-day
  // event rather than the following midnight; floor the span at one day so
  // such an event still renders instead of vanishing.
  return dayUtc >= startUtc && dayUtc < Math.max(endUtc, startUtc + ALL_DAY_MS);
}

export function buildMonthGrid(
  currentDate: Date,
  events: EventInstance[],
  weekStartDay: number = 0,
): MonthGridCell[][] {
  const monthStart = startOfMonth(currentDate);
  const gridStart = startOfWeek(monthStart, weekStartDay);

  const weeks: MonthGridCell[][] = [];
  let current = new Date(gridStart);

  for (let w = 0; w < 6; w++) {
    const week: MonthGridCell[] = [];
    for (let d = 0; d < 7; d++) {
      const cellDate = new Date(current);
      const dayEvents = events.filter((e) => eventOccupiesDay(e, cellDate));

      week.push({
        date: cellDate,
        isCurrentMonth: isSameMonth(cellDate, currentDate),
        isToday: isToday(cellDate),
        events: dayEvents,
      });

      current = addDays(current, 1);
    }
    weeks.push(week);
  }

  return weeks;
}

export function getWeekDays(currentDate: Date, weekStartDay: number = 0): Date[] {
  const weekStart = startOfWeek(currentDate, weekStartDay);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(weekStart, i));
  }
  return days;
}

// =============================================================================
// TIME GRID EVENT POSITIONING
// =============================================================================

export interface PositionedEvent {
  event: EventInstance;
  top: number;
  height: number;
  left: number;
  width: number;
  column: number;
  totalColumns: number;
}

export function positionTimedEvents(
  events: EventInstance[],
  dayDate: Date,
  hourHeightPx: number = HOUR_HEIGHT_PX,
): PositionedEvent[] {
  // Filter to timed events (not all-day) that overlap this day
  const timedEvents = events.filter((e) => {
    if (e.isAllDay) return false;
    const start = new Date(e.startAt);
    const end = new Date(e.endAt);
    return start < endOfDay(dayDate) && end > startOfDay(dayDate);
  });

  if (timedEvents.length === 0) return [];

  // Calculate pixel positions
  const positioned = timedEvents.map((event) => {
    const start = new Date(event.startAt);
    const end = new Date(event.endAt);

    // Clamp to day boundaries
    const dayStart = startOfDay(dayDate);
    const dayEnd = endOfDay(dayDate);
    const effectiveStart = start < dayStart ? dayStart : start;
    const effectiveEnd = end > dayEnd ? dayEnd : end;

    const startMinutes = effectiveStart.getHours() * 60 + effectiveStart.getMinutes();
    const endMinutes = effectiveEnd.getHours() * 60 + effectiveEnd.getMinutes();
    const durationMinutes = Math.max(endMinutes - startMinutes, 15); // Min 15 min height

    const top = (startMinutes / 60) * hourHeightPx;
    const height = (durationMinutes / 60) * hourHeightPx;

    return {
      event,
      top,
      height,
      left: 0,
      width: 100,
      column: 0,
      totalColumns: 1,
      startMinutes,
      endMinutes: startMinutes + durationMinutes,
    };
  });

  // Sort by start time, then by duration (longer events first)
  positioned.sort((a, b) => a.startMinutes - b.startMinutes || b.height - a.height);

  // Resolve overlaps using column layout
  const columns: { end: number }[] = [];

  for (const item of positioned) {
    // Find first available column
    let col = 0;
    for (col = 0; col < columns.length; col++) {
      if (columns[col].end <= item.startMinutes) break;
    }

    if (col === columns.length) {
      columns.push({ end: item.endMinutes });
    } else {
      columns[col].end = item.endMinutes;
    }

    item.column = col;
  }

  const totalColumns = columns.length;

  return positioned.map((item) => ({
    event: item.event,
    top: item.top,
    height: item.height,
    left: (item.column / totalColumns) * 100,
    width: 100 / totalColumns,
    column: item.column,
    totalColumns,
  }));
}

export function getAllDayEvents(events: EventInstance[], dayDate: Date): EventInstance[] {
  return events.filter((e) => e.isAllDay && eventOccupiesDay(e, dayDate));
}

// =============================================================================
// FORMATTING
// =============================================================================

export function formatDateLabel(date: Date, view: CalendarView): string {
  switch (view) {
    case 'month':
      return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
    case 'week': {
      const weekEnd = addDays(date, 6);
      if (date.getMonth() === weekEnd.getMonth()) {
        return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()} – ${weekEnd.getDate()}, ${date.getFullYear()}`;
      }
      return `${MONTH_NAMES[date.getMonth()]} ${date.getDate()} – ${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${date.getFullYear()}`;
    }
    case 'day':
      return `${DAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    case 'agenda':
      return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  }
}

function formatClock(hours: number, minutes: number, timeFormat: '12h' | '24h'): string {
  if (timeFormat === '24h') {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  const displayMinutes = minutes > 0 ? `:${String(minutes).padStart(2, '0')}` : '';
  return `${displayHour}${displayMinutes} ${ampm}`;
}

export function formatTime(date: Date, timeFormat: '12h' | '24h' = '12h'): string {
  return formatClock(date.getHours(), date.getMinutes(), timeFormat);
}

/**
 * A stored instant as a display time in `timeZone`, not the browser's.
 *
 * `formatTime` reads LOCAL getters, which is correct everywhere the grid
 * renders in the viewer's own zone. It is wrong inside the event dialog, whose
 * form reads and writes in the PICKER's zone: the conflict warnings listed the
 * clashing events in browser-local time right beside a start and end the user
 * had just entered in another zone, so two events that genuinely overlap could
 * be shown hours apart. Invisible until a non-local zone is chosen, which is
 * why it went unnoticed.
 */
export function formatTimeInZone(
  iso: string,
  timeZone: string,
  timeFormat: '12h' | '24h' = '12h',
): string {
  const zone = usableTimeZone(timeZone);
  const instant = new Date(iso).getTime();
  if (!Number.isFinite(instant)) return '';
  // Shifted so the UTC getters read the wall clock in `zone` — the same trick
  // `utcIsoToZonedInputs` uses, and the reason this reads UTC getters where
  // `formatTime` reads local ones.
  const shifted = new Date(instant + timeZoneOffsetMs(instant, zone));
  return formatClock(shifted.getUTCHours(), shifted.getUTCMinutes(), timeFormat);
}

export function formatTimeRange(
  startAt: string,
  endAt: string,
  isAllDay: boolean,
  timeFormat: '12h' | '24h' = '12h',
): string {
  if (isAllDay) return 'All day';
  return `${formatTime(new Date(startAt), timeFormat)} – ${formatTime(new Date(endAt), timeFormat)}`;
}

export function formatDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Inverse of `formatDateKey` — local midnight on that calendar day. */
export function parseDateKey(key: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

export function formatAgendaDate(date: Date): string {
  const today = new Date();
  const tomorrow = addDays(today, 1);

  if (isSameDay(date, today)) return 'Today';
  if (isSameDay(date, tomorrow)) return 'Tomorrow';

  return `${DAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}`;
}

// =============================================================================
// RECURRENCE LABEL
// =============================================================================

export function buildRecurrenceLabel(rule: RecurrenceRule): string {
  const freqLabel = FREQUENCY_LABELS[rule.frequency] || rule.frequency;
  let label = rule.interval > 1 ? `Every ${rule.interval} ${freqLabel}s` : `Every ${freqLabel}`;

  if (rule.frequency === 'weekly' && rule.byDay && rule.byDay.length > 0) {
    label += ` on ${rule.byDay.join(', ')}`;
  }

  if (rule.endType === 'count' && rule.count) {
    label += `, ${rule.count} times`;
  } else if (rule.endType === 'until' && rule.until) {
    const untilDate = new Date(rule.until);
    label += `, until ${MONTH_NAMES[untilDate.getMonth()]} ${untilDate.getDate()}, ${untilDate.getFullYear()}`;
  }

  return label;
}

export function parseRruleToRule(rruleStr: string | null): RecurrenceRule | null {
  if (!rruleStr) return null;

  const clean = rruleStr.replace(/^RRULE:/, '');
  const parts = new Map<string, string>();
  for (const part of clean.split(';')) {
    const [key, val] = part.split('=');
    if (key && val) parts.set(key.toUpperCase(), val);
  }

  const freqMap: Record<string, RecurrenceRule['frequency']> = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
  };

  const frequency = freqMap[parts.get('FREQ') || ''] || 'weekly';
  const interval = parseInt(parts.get('INTERVAL') || '1', 10);

  // WEEKLY only. `byDay` is the day-checkbox model, and those render plain
  // weekday tokens — an ordinal like '3TU' belongs to a MONTHLY/YEARLY rule and
  // rides in `carriedParts` instead. Populating it here regardless meant
  // opening a `FREQ=MONTHLY;BYDAY=3TU` event and switching Repeat to Weekly
  // carried '3TU' forward (RecurrenceBuilder's own `freq === 'weekly'` branch
  // re-seeds byDay from the previous rule), the checkboxes rendered with
  // nothing selected because '3TU' is not in RRULE_DAYS, and saving emitted
  // `FREQ=WEEKLY;BYDAY=3TU` — an ordinal BYDAY on a weekly rule, invalid per
  // RFC 5545 and rejected by Graph.
  const byDay = frequency === 'weekly' ? parts.get('BYDAY')?.split(',') : undefined;

  let endType: RecurrenceRule['endType'] = 'never';
  let count: number | undefined;
  let until: string | undefined;

  if (parts.has('COUNT')) {
    endType = 'count';
    count = parseInt(parts.get('COUNT')!, 10);
  } else if (parts.has('UNTIL')) {
    endType = 'until';
    const untilStr = parts.get('UNTIL')!;
    // Parse YYYYMMDDTHHMMSSZ format
    const match = untilStr.match(/^(\d{4})(\d{2})(\d{2})/);
    if (match) {
      until = `${match[1]}-${match[2]}-${match[3]}`;
    }
  }

  // Everything the builder does not model, kept verbatim. Without this the
  // dialog REWRITES the rule it was only meant to read.
  const carriedParts: Array<[string, string]> = [];
  for (const [key, value] of parts) {
    if (MODELED_RRULE_PARTS.has(key)) continue;
    // BYDAY is modelled by the day checkboxes for a WEEKLY rule only. On any
    // other frequency it means something the builder has no control for
    // ('3TU' = the third Tuesday), so it has to ride along.
    if (key === 'BYDAY' && frequency === 'weekly') continue;
    carriedParts.push([key, value]);
  }

  return {
    frequency,
    interval,
    byDay,
    endType,
    count,
    until,
    weekStart: parts.get('WKST'),
    untilRaw: parts.get('UNTIL'),
    ...(carriedParts.length > 0 ? { carriedParts, carriedFrequency: frequency } : {}),
  };
}

/**
 * Rule parts the builder actually has a control for. Everything else is
 * carried through verbatim — see `RecurrenceRule.carriedParts`.
 */
const MODELED_RRULE_PARTS = new Set(['FREQ', 'INTERVAL', 'COUNT', 'UNTIL', 'WKST']);

export function buildRruleString(rule: RecurrenceRule): string {
  const freqMap: Record<string, string> = {
    daily: 'DAILY',
    weekly: 'WEEKLY',
    monthly: 'MONTHLY',
    yearly: 'YEARLY',
  };

  // The bare RRULE VALUE, with no `RRULE:` property prefix. That prefix is the
  // iCalendar property name and belongs only in a serialized .ics line — the
  // vault column stores the value. Provider sync stores the bare form (inbound
  // mappers strip the prefix), so writing the prefixed form here made a single
  // calendar hold both, and pushing such an event produced `RRULE:RRULE:FREQ=…`
  // which Google rejects with 400. `parseRruleToRule` accepts either form, so
  // events already stored with the prefix keep reading correctly.
  let rrule = `FREQ=${freqMap[rule.frequency]}`;

  if (rule.interval > 1) {
    rrule += `;INTERVAL=${rule.interval}`;
  }

  if (rule.frequency === 'weekly' && rule.byDay && rule.byDay.length > 0) {
    rrule += `;BYDAY=${rule.byDay.join(',')}`;
  }

  // Re-emitted only because they were there — the same reason WKST and the
  // UNTIL token below are. Dropped when the user CHANGED the frequency: a
  // BYMONTHDAY read off a MONTHLY rule is meaningless on the WEEKLY rule that
  // replaced it, and carrying it would build something the user never asked
  // for.
  if (rule.carriedParts && rule.carriedFrequency === rule.frequency) {
    for (const [key, value] of rule.carriedParts) {
      rrule += `;${key}=${value}`;
    }
  }

  // Emitted only because it was there. The builder offers no control for week
  // start, so re-emitting what the rule already carried is the difference
  // between reading a rule and REWRITING it — and rules from Apple and Outlook
  // nearly all carry WKST=SU.
  if (rule.weekStart) {
    rrule += `;WKST=${rule.weekStart}`;
  }

  if (rule.endType === 'count' && rule.count) {
    rrule += `;COUNT=${rule.count}`;
  } else if (rule.endType === 'until' && rule.until) {
    // `rule.until` is already a plain 'YYYY-MM-DD'. Round-tripping it through
    // Date lost a day for everyone west of UTC: `new Date('2032-03-26')` is UTC
    // midnight, and reading it back with LOCAL getters gives the 25th. That
    // silently truncated the series by a day on every save.
    // Re-emit the original token when the DATE is unchanged: the builder has
    // no time-of-day control, so synthesising 23:59:59Z over a provider's
    // 05:59:59Z would move the series end on a save that changed nothing.
    // `rule.until` may be a plain 'YYYY-MM-DD' (from parseRruleToRule) or a
    // full ISO datetime (from the builder's own input), so take the date part
    // of either rather than assuming one shape.
    const asDate = rule.until.slice(0, 10).replace(/-/g, '');
    const untilStr =
      rule.untilRaw && rule.untilRaw.startsWith(asDate) ? rule.untilRaw : `${asDate}T235959Z`;
    rrule += `;UNTIL=${untilStr}`;
  }

  return rrule;
}

// =============================================================================
// NAVIGATION
// =============================================================================

export function navigateDate(
  currentDate: Date,
  view: CalendarView,
  direction: 'prev' | 'next',
): Date {
  const delta = direction === 'next' ? 1 : -1;

  switch (view) {
    case 'month':
      return addMonths(currentDate, delta);
    case 'week':
      return addWeeks(currentDate, delta);
    case 'day':
      return addDays(currentDate, delta);
    case 'agenda':
      return addDays(currentDate, delta * 7);
  }
}

// =============================================================================
// VIDEO CALL DETECTION
// =============================================================================

export type VideoProvider = 'zoom' | 'meet' | 'teams' | 'webex';

export interface VideoCallInfo {
  provider: VideoProvider;
  url: string;
}

const VIDEO_PATTERNS: { provider: VideoProvider; regex: RegExp }[] = [
  { provider: 'zoom', regex: /https?:\/\/[\w.-]*zoom\.us\/j\/\S+/ },
  { provider: 'meet', regex: /https?:\/\/meet\.google\.com\/[\w-]+/ },
  { provider: 'teams', regex: /https?:\/\/teams\.microsoft\.com\/l\/meetup-join\/\S+/ },
  { provider: 'webex', regex: /https?:\/\/[\w.-]*webex\.com\/meet\/\S+/ },
];

export function detectVideoLink(event: {
  url?: string;
  location?: string;
  description?: string;
}): VideoCallInfo | null {
  const fields = [event.url, event.location, event.description];
  for (const field of fields) {
    if (!field) continue;
    for (const { provider, regex } of VIDEO_PATTERNS) {
      const match = field.match(regex);
      if (match) {
        return { provider, url: match[0] };
      }
    }
  }
  return null;
}

export const VIDEO_PROVIDER_LABELS: Record<VideoProvider, string> = {
  zoom: 'Join Zoom',
  meet: 'Join Meet',
  teams: 'Join Teams',
  webex: 'Join Webex',
};

// =============================================================================
// CONFLICT DETECTION
// =============================================================================

/**
 * Find events that overlap with a proposed time range.
 * Ignores all-day events and optionally excludes the event being edited.
 */
export function findConflicts(
  startAt: string,
  endAt: string,
  events: EventInstance[],
  excludeEventId?: string,
): EventInstance[] {
  const start = new Date(startAt);
  const end = new Date(endAt);

  return events.filter((e) => {
    if (e.isAllDay) return false;
    if (excludeEventId && e.eventId === excludeEventId) return false;
    const eStart = new Date(e.startAt);
    const eEnd = new Date(e.endAt);
    return eStart < end && eEnd > start;
  });
}

/**
 * Given a list of timed events for a single day, return the set of event IDs
 * that overlap with at least one other event.
 */
export function findOverlappingEventIds(events: EventInstance[]): Set<string> {
  const timed = events.filter((e) => !e.isAllDay);
  const overlapping = new Set<string>();

  for (let i = 0; i < timed.length; i++) {
    const aStart = new Date(timed[i].startAt);
    const aEnd = new Date(timed[i].endAt);
    for (let j = i + 1; j < timed.length; j++) {
      const bStart = new Date(timed[j].startAt);
      const bEnd = new Date(timed[j].endAt);
      if (aStart < bEnd && bStart < aEnd) {
        overlapping.add(timed[i].eventId);
        overlapping.add(timed[j].eventId);
      }
    }
  }

  return overlapping;
}

// =============================================================================
// BREAK GAP DETECTION
// =============================================================================

export interface BreakWarning {
  eventBefore: EventInstance;
  eventAfter: EventInstance;
  gapMinutes: number;
}

/**
 * Find pairs of adjacent (non-overlapping) events on a given day where the gap
 * between them is positive but less than `minBreakMinutes`.
 */
export function findInsufficientBreaks(
  events: EventInstance[],
  day: Date,
  minBreakMinutes: number,
): BreakWarning[] {
  if (minBreakMinutes <= 0) return [];

  const dayStart = startOfDay(day);
  const dayEnd = endOfDay(day);

  const timed = events
    .filter((e) => {
      if (e.isAllDay) return false;
      const s = new Date(e.startAt);
      const end = new Date(e.endAt);
      return s < dayEnd && end > dayStart;
    })
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const warnings: BreakWarning[] = [];

  for (let i = 0; i < timed.length - 1; i++) {
    const endA = new Date(timed[i].endAt);
    const startB = new Date(timed[i + 1].startAt);
    const gapMs = startB.getTime() - endA.getTime();

    if (gapMs > 0 && gapMs < minBreakMinutes * 60 * 1000) {
      warnings.push({
        eventBefore: timed[i],
        eventAfter: timed[i + 1],
        gapMinutes: Math.round(gapMs / 60000),
      });
    }
  }

  return warnings;
}

// =============================================================================
// EVENT COLOR
// =============================================================================

export function getEventColor(
  event: EventInstance,
  colorPriority: 'event' | 'calendar' = 'event',
): string {
  if (colorPriority === 'calendar') {
    return event.calendarColor || event.color || '#3b82f6';
  }
  return event.color || event.calendarColor || '#3b82f6';
}

// =============================================================================
// WEATHER
// =============================================================================

/** Exact conversion; the rounding for display happens in `formatTemperature`. */
export function celsiusToFahrenheit(celsius: number): number {
  return celsius * (9 / 5) + 32;
}

/**
 * Render a Celsius reading in the user's unit. Forecast data is stored in
 * Celsius everywhere (see `WeatherDay`), so this is the only place the unit
 * choice is applied.
 */
export function formatTemperature(celsius: number, unit: TemperatureUnit = 'fahrenheit'): string {
  const value = unit === 'celsius' ? celsius : celsiusToFahrenheit(celsius);
  return `${Math.round(value)}°`;
}

// =============================================================================
// EVENT DRAFTS
// =============================================================================

/**
 * The time a click on a whole day (month view) should default an event to.
 *
 * Working-hours start is the predictable answer for any other day. For today
 * that hour is often already past, so fall back to the next snap boundary from
 * now — the same behaviour as reaching for the calendar mid-afternoon and
 * expecting the new event to land ahead of you rather than behind.
 */
export function defaultEventStartForDay(
  date: Date,
  workingHoursStart: number,
  snapIntervalMinutes: number = 15,
  now: Date = new Date(),
): Date {
  const start = startOfDay(date);
  start.setHours(workingHoursStart, 0, 0, 0);
  if (!isSameDay(date, now) || now <= start) return start;

  const snap = Math.max(1, snapIntervalMinutes);
  const next = new Date(now);
  next.setSeconds(0, 0);
  next.setMinutes(Math.ceil(next.getMinutes() / snap) * snap);
  // Rolling past midnight would move the event off the clicked day.
  return isSameDay(next, date) ? next : start;
}

/**
 * Build an all-day draft covering `from`..`to` inclusive.
 *
 * The end is the EXCLUSIVE UTC midnight after the last day — the same
 * convention EventDialog writes, so a dragged event and a hand-entered one
 * round-trip identically. (It was `23:59:59.999` local; the body below has
 * produced the exclusive-UTC-midnight form since the all-day date fix, and
 * this docstring described the encoding it replaced.)
 */
export function buildAllDayDraft(from: Date, to: Date): QuickEventDraft {
  const [first, last] = from <= to ? [from, to] : [to, from];
  // All-day is a DATE, so it is encoded the same way the event dialog encodes
  // it — UTC midnight with an exclusive UTC-midnight end. This used to use
  // local startOfDay/endOfDay, which put every user east of UTC one day early:
  // the stored instant fell on the previous UTC date, and the grid reads all-day
  // events by UTC date components. Fixing only the dialog left the two creation
  // paths disagreeing about what an all-day event is.
  const { startAt, endAt } = encodeAllDayRange(formatDateKey(first), formatDateKey(last));
  return { start: new Date(startAt), end: new Date(endAt), isAllDay: true };
}

/** Build a draft covering `durationMinutes` from `start`. */
export function buildTimedDraft(start: Date, durationMinutes: number): QuickEventDraft {
  return {
    start: new Date(start),
    end: new Date(start.getTime() + Math.max(1, durationMinutes) * 60000),
    isAllDay: false,
  };
}

/**
 * Whether a delete can be undone, and therefore whether the delete toast
 * offers an Undo.
 *
 *   - non-recurring / `all` -> soft-deletes the event row (`deleted_at`).
 *     Undone by `POST /events/:id/restore`, and the restore pushes cleanly to
 *     every provider because it is an ordinary event update.
 *   - `this` -> LOCALLY reversible (the server can clear the deletion
 *     exception, and `restoreVaultCalendarEventOccurrence` does exactly that)
 *     but DELIBERATELY not offered. A restored exception carries no override
 *     fields, so `overrideFieldGroups` derives an empty set and the engine
 *     pushes `{}` — Google leaves the occurrence `cancelled`, Microsoft 404s
 *     on a deleted instance, and the engine then records the empty result as
 *     the new sync base and never retries. The occurrence would come back in
 *     Fias and stay gone on the phone, permanently and silently. Apple works
 *     only incidentally, because its provider strips the EXDATE while
 *     upserting an override. Re-enable once there is a provider-neutral
 *     "resurrect occurrence" operation with end-to-end sync tests.
 *   - `this_and_future` -> overwrites the series rrule and HARD-deletes future
 *     exception rows. Nothing to restore from at all.
 *
 * A button that silently fails to restore is worse than no button.
 */
export function isDeleteReversible(
  event: { isRecurring?: boolean },
  scope: RecurrenceEditScope,
): boolean {
  return !event.isRecurring || scope === 'all';
}

/**
 * Whether an edit at this scope moves the series MASTER's own start.
 *
 * `all` does. So does `this_and_future`: it overwrites the series rrule (see
 * `isDeleteReversible` above). Only `this` leaves the master alone. No scope at
 * all means a non-recurring event, which is trivially its own master.
 *
 * Load-bearing, because a weekly master starting on a day its own BYDAY
 * excludes is invalid — Graph refuses the push, and locally the series expands
 * from a start no occurrence matches, so the moved occurrence vanishes. The
 * rule has to be brought along with the start.
 *
 * Extracted because the two callers disagreed: the dialog's save included
 * `this_and_future`, the grid's drag-move tested `scope === 'all'` only, so
 * dragging a "this and following" edit skipped the adjustment the dialog knew
 * to make. Same decision, two answers, in files that get edited together.
 */
export function scopeTouchesSeries(scope?: RecurrenceEditScope): boolean {
  return !scope || scope === 'all' || scope === 'this_and_future';
}

/**
 * The `instanceDate` a scoped (`this` / `this_and_future`) event mutation must
 * send — the occurrence's ORIGINAL start, i.e. the slot the recurrence rule
 * generated it in.
 *
 * `originalStartAt` is only populated on EXCEPTION instances — occurrences
 * that were already moved or overridden. For an ordinary expanded occurrence
 * it is undefined, and its original start is simply its `startAt`, because
 * nothing has shifted it. Sending undefined makes the server reject the whole
 * request with "instanceDate required for scope='this'", which is exactly
 * what deleting one occurrence of a series used to do.
 */
export function occurrenceInstanceDate(event: {
  originalStartAt?: string;
  startAt: string;
}): string {
  return event.originalStartAt ?? event.startAt;
}
